import type { Placement } from '@floating-ui/react';
import { FloatingPortal } from '@floating-ui/react';
import type { FC, ReactElement, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { TooltipProvider } from '../../context/TooltipContext';
import { useTooltip } from '../../hooks/useTooltip';

type TooltipProps = {
  text: ReactElement | ReactNode | string | number | boolean | null | undefined;
  truncatedTextMode?: boolean; // when true, search for truncated text, and only show the tooltip if found
  children: (props: Record<string, unknown>) => ReactElement | HTMLElement;
  style?: 'dark' | 'light';
  /** Preferred side of the anchor (auto-flips if there's no room). Defaults to `'bottom'`. */
  placement?: Placement;
  /** Visual only: the anchor's accessible name already says this, so screen readers skip it. */
  decorative?: boolean;
};

const Tooltip: FC<TooltipProps> = (props) => {
  const { text, truncatedTextMode, children, style = 'dark', placement, decorative } = props;

  const { isActive, setIsBlockedByChild, anchorProps, floatingProps, arrowEl } = useTooltip(
    truncatedTextMode,
    'tooltip',
    !!text,
    placement,
    decorative,
  );

  let styleCss: string;
  switch (style) {
    case 'dark':
    default:
      styleCss = 'text-white! bg-primary-100 border-primary-100 [&_[class*="text-"]]:text-white!';
      break;
    case 'light':
      styleCss = 'text-primary-100! bg-white border-greyscale-15 [&_[class*="text-"]]:text-primary-100!';
      break;
  }

  const contextValue = useMemo(() => ({ setBlocking: setIsBlockedByChild }), [setIsBlockedByChild]);

  return (
    <>
      <TooltipProvider value={contextValue}>
        <>{children(anchorProps)}</>
      </TooltipProvider>
      {typeof text !== 'boolean' && !!text && isActive && (
        <FloatingPortal id="tooltip-root">
          <div className={`${style} ${styleCss} override-link-colour rounded-md border px-3 py-2 shadow-md`} {...floatingProps}>
            {arrowEl}
            {text}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default React.memo(Tooltip);
